import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiStar,
  FiAward,
  FiAlertCircle,
  FiChevronRight,
  FiCheckCircle,
  FiLoader,
} from 'react-icons/fi';
import HoneymoonBookingSection from './HoneymoonBookingSection';
import './HoneymoonDetailsPage.css';
import Navber from '../../../Components/Navber';

// --- Breadcrumb ---
const Breadcrumb = ({ items }) => (
  <nav className="pkgd-breadcrumb">
    <div className="pkgd-breadcrumb-inner">
      {items.map((item, index) => (
        <span key={index} className="pkgd-breadcrumb-item">
          {index > 0 && <FiChevronRight className="pkgd-breadcrumb-sep" />}
          {item.href ? (
            <Link to={item.href}>{item.label}</Link>
          ) : (
            <span className="pkgd-breadcrumb-current">{item.label}</span>
          )}
        </span>
      ))}
    </div>
  </nav>
);

// --- Plan Card (for displaying plan details in a card) ---
const PlanCard = ({ plan, isSelected, onSelect }) => {
  const price = plan.couple_price || plan.price_per_person * (plan.pax_count || 2);
  return (
    <div
      className={`pkgd-plan-card ${isSelected ? 'pkgd-selected' : ''}`}
      onClick={() => onSelect(plan.id)}
    >
      <div className="pkgd-plan-header">
        <span className="pkgd-plan-tier">{plan.tier_label || plan.tier}</span>
        <span className="pkgd-plan-price">₹{price.toLocaleString()}</span>
      </div>
      <h4>{plan.plan_name}</h4>
      <p className="pkgd-plan-pax">Up to {plan.pax_count} guests</p>
      <div className="pkgd-plan-badges">
        {plan.has_veg_food && <span className="pkgd-tag pkgd-tag-veg">Veg</span>}
        {plan.has_nonveg_food && <span className="pkgd-tag pkgd-tag-nonveg">Non‑Veg</span>}
      </div>
      <div
        className="pkgd-plan-summary"
        dangerouslySetInnerHTML={{ __html: plan.summary || '' }}
      />
      {isSelected && <FiCheckCircle className="pkgd-plan-check" />}
    </div>
  );
};

// --- Itinerary Day Component ---
const ItineraryDay = ({ day }) => (
  <div className="pkgd-itinerary-day">
    <div className="pkgd-day-number">Day {day?.day_number}</div>
    <h4>{day?.title}</h4>
    <div
      className="pkgd-day-description"
      dangerouslySetInnerHTML={{ __html: day?.description }}
    />
    {day?.night_stay_city && (
      <p className="pkgd-day-stay"><FiMapPin /> Night stay: {day?.night_stay_city}</p>
    )}
    {day?.addons && day.addons.length > 0 && (
      <div className="pkgd-day-addons">
        <span className="pkgd-addon-label">Add‑ons available:</span>
        {day?.addons.map((a) => (
          <span key={a.id} className="pkgd-addon-tag">
            {a?.addon_name} (₹{a?.price_per_person}/person)
          </span>
        ))}
      </div>
    )}
  </div>
);

// --- Main Component ---
export default function HoneymoonDetailsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const bookingRef = useRef(null);

  const [packageData, setPackageData] = useState(null);
  const [plans, setPlans] = useState([]); // flattened array of all plans
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('plans');
  const [hideMobileBar, setHideMobileBar] = useState(false);

  // --- State for selected plan details (fetched from /honeymoon/plans/{id}) ---
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlanDetail, setSelectedPlanDetail] = useState(null);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);
  const [planDetailError, setPlanDetailError] = useState(null);

  // --- Fetch package and plans ---
  useEffect(() => {
    const fetchPackage = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `https://admin.jayrajgroup.com/api/honeymoon/packages/${slug}`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        if (!result.status || !result.data) {
          throw new Error(result.message || 'Failed to fetch package');
        }
        const pkg = result.data.package;
        const plansObj = result.data.plans || {};
        const allPlans = [
          ...(plansObj.basic || []),
          ...(plansObj.standard || []),
          ...(plansObj.premium || []),
        ];
        setPackageData(pkg);
        setPlans(allPlans);

        // Auto-select the first plan (Basic) if available
        if (allPlans.length > 0) {
          setSelectedPlanId(allPlans[0].id);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchPackage();
  }, [slug]);

  // --- Fetch plan details when selectedPlanId changes ---
  useEffect(() => {
    if (!selectedPlanId) return;

    const fetchPlanDetail = async () => {
      setPlanDetailLoading(true);
      setPlanDetailError(null);
      try {
        const response = await fetch(
          `https://admin.jayrajgroup.com/api/honeymoon/plans/${selectedPlanId}`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        if (!result.status || !result.data) {
          throw new Error(result.message || 'Failed to fetch plan details');
        }
        setSelectedPlanDetail(result.data);
      } catch (err) {
        setPlanDetailError(err.message);
        setSelectedPlanDetail(null);
      } finally {
        setPlanDetailLoading(false);
      }
    };

    fetchPlanDetail();
  }, [selectedPlanId]);

  // --- Hide the mobile bottom bar when the booking section enters view ---
  useEffect(() => {
    if (!bookingRef.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setHideMobileBar(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px 0px -15% 0px',
        threshold: 0,
      }
    );

    observer.observe(bookingRef.current);
    return () => observer.disconnect();
  }, [packageData, loading]);

  // --- Handlers ---
  const handleGoBack = () => navigate(-1);

  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);
    // If we're on the plans tab, stay there; otherwise, switch to it
    if (activeTab !== 'plans') {
      setActiveTab('plans');
    }
  };

  // --- Loading / Error states ---
  if (loading) {
    return (
      <div className="pkgd-page-wrapper">
        <div className="pkgd-container">
          <div className="pkgd-loading-state">
            <div className="pkgd-skeleton-hero"></div>
            <div className="pkgd-skeleton-content">
              <div className="pkgd-skeleton-title"></div>
              <div className="pkgd-skeleton-text"></div>
              <div className="pkgd-skeleton-grid">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="pkgd-skeleton-card"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !packageData) {
    return (
      <div className="pkgd-page-wrapper">
        <div className="pkgd-container">
          <div className="pkgd-error-state">
            <FiAlertCircle className="pkgd-error-icon" />
            <h3>Package Not Found</h3>
            <p>{error || 'The honeymoon package you are looking for does not exist.'}</p>
            <button onClick={handleGoBack} className="pkgd-btn-primary">
              <FiArrowLeft /> Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const destinationName = packageData.destination?.name || '';

  // Get the currently selected plan's itinerary from the fetched detail
  const currentPlanItinerary = selectedPlanDetail?.itinerary;

  return (
    <div className="pkgd-page-wrapper">
      <Navber />
      <div className="pkgd-container">
        <Breadcrumb
          items={[
            { label: 'Home', href: '/' },
            { label: 'Honeymoon', href: '/honeymoon/packages' },
            { label: packageData.name },
          ]}
        />

        {/* Hero */}
        <section className="pkgd-hero">
          <div
            className="pkgd-hero-bg"
            style={{
              backgroundImage: `url(${packageData.banner_url || packageData.featured_photo_url})`,
            }}
          >
            <div className="pkgd-hero-overlay"></div>
          </div>
          <div className="pkgd-hero-content">
            <button className="pkgd-btn-back" onClick={handleGoBack}>
              <FiArrowLeft /> Back
            </button>
            <div className="pkgd-hero-text">
              <div className="pkgd-hero-badge">
                <FiStar /> <span>Honeymoon Package</span>
              </div>
              <h1 className="pkgd-hero-title">{packageData.name}</h1>
              <p className="pkgd-hero-description">
                {packageData.short_description || 'A romantic getaway just for two.'}
              </p>
              <div className="pkgd-hero-stats">
                <div className="pkgd-stat-item">
                  <FiCalendar /> <span>{packageData.duration_days} Days</span>
                </div>
                <div className="pkgd-stat-item">
                  <FiClock /> <span>{packageData.duration_nights} Nights</span>
                </div>
                {destinationName && (
                  <div className="pkgd-stat-item">
                    <FiMapPin /> <span>{destinationName}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main Grid */}
        <div className="pkgd-main-grid">
          <div className="pkgd-details-column">
            {/* Tabs — Plans → Itinerary → Overview */}
            <div className="pkgd-tabs">
              <button
                className={`pkgd-tab-btn ${activeTab === 'plans' ? 'pkgd-active' : ''}`}
                onClick={() => setActiveTab('plans')}
              >
                Plans
                {plans.length > 0 && <span className="pkgd-tab-count">{plans.length}</span>}
              </button>

              <button
                className={`pkgd-tab-btn ${activeTab === 'itinerary' ? 'pkgd-active' : ''}`}
                onClick={() => setActiveTab('itinerary')}
              >
                Itinerary
              </button>

              <button
                className={`pkgd-tab-btn ${activeTab === 'overview' ? 'pkgd-active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
            </div>

            {/* Tab Content */}
            <div className="pkgd-tab-content">
              {/* --- OVERVIEW TAB --- */}
              {activeTab === 'overview' && (
                <div className="pkgd-overview">
                  <div
                    className="pkgd-description"
                    dangerouslySetInnerHTML={{
                      __html: packageData.description || '<p>No description available.</p>',
                    }}
                  />
                  <div className="pkgd-highlights">
                    <h3>Package Highlights</h3>
                    <div className="pkgd-highlights-grid">
                      <div className="pkgd-highlight-item">
                        <FiAward /> <span>Romantic Getaway</span>
                      </div>
                      <div className="pkgd-highlight-item">
                        <FiCalendar /> <span>Flexible Dates</span>
                      </div>
                      <div className="pkgd-highlight-item">
                        <FiMapPin /> <span>Scenic Destinations</span>
                      </div>
                      <div className="pkgd-highlight-item">
                        <FiStar /> <span>Luxury Stays</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- ITINERARY TAB --- */}
              {activeTab === 'itinerary' && (
                <div className="pkgd-itinerary">
                  {planDetailLoading ? (
                    <div className="pkgd-loading-spinner">
                      <FiLoader className="pkgd-spinner" />
                      <span>Loading itinerary...</span>
                    </div>
                  ) : planDetailError ? (
                    <div className="pkgd-error-state-small">
                      <FiAlertCircle />
                      <p>{planDetailError}</p>
                    </div>
                  ) : currentPlanItinerary ? (
                    <>
                      <h3>{currentPlanItinerary.name}</h3>
                      <p>
                        {currentPlanItinerary.total_days} Days / {currentPlanItinerary.total_nights} Nights
                      </p>
                      <div className="pkgd-itinerary-days">
                        {currentPlanItinerary.days?.map((day) => (
                          <ItineraryDay key={day.day_number} day={day} />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="pkgd-empty-state">
                      <FiAlertCircle />
                      <p>No itinerary available for the selected plan.</p>
                    </div>
                  )}
                </div>
              )}

              {/* --- PLANS TAB --- */}
              {activeTab === 'plans' && (
                <div className="pkgd-plans">
                  {plans.length === 0 ? (
                    <div className="pkgd-empty-state">
                      <FiAlertCircle />
                      <p>No plans available for this package.</p>
                    </div>
                  ) : (
                    <>
                      <div className="pkgd-plans-grid">
                        {plans.map((plan) => (
                          <PlanCard
                            key={plan.id}
                            plan={plan}
                            isSelected={selectedPlanId === plan.id}
                            onSelect={handlePlanSelect}
                          />
                        ))}
                      </div>

                      {/* Show selected plan's itinerary inside Plans tab as well */}
                      {selectedPlanId && (
                        <div className="pkgd-plan-itinerary-preview">
                          <h4>
                            Itinerary for {plans.find((p) => p.id === selectedPlanId)?.plan_name}
                          </h4>
                          {planDetailLoading ? (
                            <div className="pkgd-loading-spinner">
                              <FiLoader className="pkgd-spinner" />
                              <span>Loading itinerary...</span>
                            </div>
                          ) : planDetailError ? (
                            <div className="pkgd-error-state-small">
                              <FiAlertCircle />
                              <p>{planDetailError}</p>
                            </div>
                          ) : currentPlanItinerary ? (
                            <div className="pkgd-itinerary-days">
                              {currentPlanItinerary.days?.map((day) => (
                                <ItineraryDay key={day.day_number} day={day} />
                              ))}
                            </div>
                          ) : (
                            <p>No itinerary details available.</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Booking Section */}
          <div ref={bookingRef} id="pkgd-booking-anchor">
            <HoneymoonBookingSection
              packageData={packageData}
              destinationName={destinationName}
              plans={plans}
            />
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar — duration left, Book Now right */}
      <div
        className={`pkgd-mobile-bar ${hideMobileBar ? 'pkgd-mobile-bar-hidden' : ''}`}
        role="region"
        aria-label="Quick actions"
        aria-hidden={hideMobileBar}
      >
        <div className="pkgd-mobile-bar-duration">
          <FiCalendar aria-hidden="true" />
          <span>
            {packageData.duration_days}D
            <span className="pkgd-mobile-bar-sep">/</span>
            {packageData.duration_nights}N
          </span>
        </div>

        <a
          href="#pkgd-booking-anchor"
          className="pkgd-mobile-bar-cta"
          onClick={(e) => {
            e.preventDefault();
            bookingRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }}
        >
          Book Now
          <FiChevronRight aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}